/**
 * formContexts.js — FILL_FORM v1 descriptors para formulários simples.
 *
 * O TrainingBuilderWizard tem descriptor próprio (applyAssistPatch.js no
 * builder, junto do setContract). Aqui ficam os formulários planos.
 *
 * Contrato: LLM sugere patch → ESTE módulo valida → usuário já confirmou
 * no FillFormBubble → o save continua sendo o botão normal da tela
 * (submitWellness → POST /my/wellness/checkin). Aplicar NUNCA salva.
 */

// Espelho do BODY_PARTS de WellnessPage.jsx — ids canônicos do body map.
export const WELLNESS_BODY_PARTS = [
  'ombro_d', 'ombro_e', 'pescoco', 'peitoral', 'lombar',
  'cotovelo_d', 'cotovelo_e', 'punho_d', 'punho_e',
  'quadril_d', 'quadril_e', 'joelho_d', 'joelho_e',
  'tornozelo_d', 'tornozelo_e', 'pe_d', 'pe_e', 'costas_alta', 'abdomen',
];

/** Check-in de wellness do atleta — schema REAL do WellnessPage (INITIAL_FORM). */
export function buildWellnessCheckinFormContext(form = {}) {
  return {
    form_id: 'wellness_checkin',
    schema_version: 'v1',
    current_values: {
      sleep_hours: form.sleep_hours ?? null,
      sleep_quality: form.sleep_quality ?? null,
      fatigue_level: form.fatigue_level ?? null,
      mood_level: form.mood_level ?? null,
      soreness_level: form.soreness_level ?? null,
      injury_flag: form.injury_flag ?? false,
      pain_locations: form.pain_locations ?? [],
      notes: form.notes ?? '',
    },
    missing_required_fields: [],
    allowed_values: {
      sleep_hours: '0 a 12 (passo 0.5)',
      sleep_quality: [1, 2, 3, 4, 5],
      fatigue_level: [1, 2, 3, 4, 5],
      mood_level: [1, 2, 3, 4, 5],
      soreness_level: [0, 1, 2, 3, 4, 5],
      pain_locations: WELLNESS_BODY_PARTS,
    },
  };
}

const SCALE_FIELDS = {
  sleep_quality: [1, 5],
  fatigue_level: [1, 5],
  mood_level: [1, 5],
  soreness_level: [0, 5],
};

/**
 * Valida patch do agente contra o schema wellness_checkin v1.
 * Campos não mencionados no patch ficam INALTERADOS; escala fora do
 * intervalo é RECUSADA (nunca clampada); campo desconhecido é rejeitado
 * explicitamente (nunca ignorado em silêncio).
 *
 * @param {object} form estado atual do form (INITIAL_FORM shape)
 * @param {object} patch patch do agente (payload.patch do fill_form)
 * @returns {{ nextForm: object, applied: string[], rejected: {field: string, reason: string}[] }}
 */
export function applyWellnessPatch(form, patch) {
  const applied = [];
  const rejected = [];
  const next = { ...form };

  if (!patch || typeof patch !== 'object') {
    return { nextForm: form, applied, rejected: [{ field: '*', reason: 'patch inválido' }] };
  }

  for (const [field, value] of Object.entries(patch)) {
    if (field in SCALE_FIELDS) {
      const [min, max] = SCALE_FIELDS[field];
      const n = Number(value);
      if (Number.isInteger(n) && n >= min && n <= max) { next[field] = n; applied.push(field); }
      else rejected.push({ field, reason: `fora da escala ${min}-${max}` });
    } else if (field === 'sleep_hours') {
      const n = Number(value);
      if (Number.isFinite(n) && n >= 0 && n <= 12) {
        next.sleep_hours = Math.round(n * 2) / 2; applied.push(field);
      } else rejected.push({ field, reason: 'horas de sono devem estar entre 0 e 12' });
    } else if (field === 'injury_flag') {
      if (typeof value === 'boolean') { next.injury_flag = value; applied.push(field); }
      else rejected.push({ field, reason: 'deve ser boolean' });
    } else if (field === 'pain_locations') {
      // Atômico: uma região inválida rejeita o campo inteiro — não aplica metade.
      if (Array.isArray(value) && value.every((p) => WELLNESS_BODY_PARTS.includes(p))) {
        next.pain_locations = [...value]; applied.push(field);
      } else rejected.push({ field, reason: 'região corporal fora do body map' });
    } else if (field === 'notes') {
      if (typeof value === 'string' && value.length <= 1000) { next.notes = value; applied.push(field); }
      else rejected.push({ field, reason: 'notas inválidas (máx 1000 chars)' });
    } else {
      rejected.push({ field, reason: 'campo desconhecido no schema wellness_checkin v1' });
    }
  }

  return { nextForm: next, applied, rejected };
}

// ─────────────────────────────────────────────────────────────
// User Profile (COMPLETE_PROFILE, 26/08) — schema do ProfileSection
// de UserProfilePage. Email é identidade de login — NUNCA via patch.
// ─────────────────────────────────────────────────────────────

export const PROFILE_GENDERS = ['M', 'F', 'other', 'masculino', 'feminino', 'outro'];
const PROFILE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PROFILE_TEXT_FIELDS = { fullName: 120, phone: 30, city: 80, state: 40, specialty: 80, bio: 1000 };

/** Contexto do form user_profile p/ o orchestrator (COMPLETE_PROFILE). */
export function buildUserProfileFormContext(form = {}) {
  const missing = [];
  if (!form.fullName) missing.push('fullName');
  if (!form.phone) missing.push('phone');
  if (!form.city) missing.push('city');
  if (!form.dateOfBirth) missing.push('birth_date');
  if (!form.gender) missing.push('gender');
  return {
    form_id: 'user_profile',
    schema_version: 'v1',
    current_values: {
      fullName: form.fullName ?? '',
      phone: form.phone ?? '',
      city: form.city ?? '',
      state: form.state ?? '',
      specialty: form.specialty ?? '',
      bio: form.bio ?? '',
      gender: form.gender ?? null,
      dateOfBirth: form.dateOfBirth ?? null,
    },
    missing_required_fields: missing,
    allowed_values: {
      gender: PROFILE_GENDERS,
      dateOfBirth: 'YYYY-MM-DD',
    },
  };
}

/**
 * Valida patch do agente contra o schema user_profile v1.
 * Email/senha/ids são identidade — rejeitados. Delta: campos não
 * mencionados ficam intactos.
 */
export function applyUserProfilePatch(form, patch) {
  const applied = [];
  const rejected = [];
  const next = { ...form };

  if (!patch || typeof patch !== 'object') {
    return { nextForm: form, applied, rejected: [{ field: '*', reason: 'patch inválido' }] };
  }

  for (const [field, value] of Object.entries(patch)) {
    if (['email', 'password', 'user_id', 'id', 'avatar'].includes(field)) {
      rejected.push({ field, reason: 'campo de identidade/credencial — não patchável pelo Assist' });
    } else if (field in PROFILE_TEXT_FIELDS) {
      if (typeof value === 'string' && value.trim() && value.length <= PROFILE_TEXT_FIELDS[field]) {
        next[field] = value.trim(); applied.push(field);
      } else rejected.push({ field, reason: 'texto inválido ou longo demais' });
    } else if (field === 'gender') {
      if (PROFILE_GENDERS.includes(value)) { next.gender = value; applied.push(field); }
      else rejected.push({ field, reason: 'gênero fora do enum' });
    } else if (field === 'dateOfBirth' || field === 'birth_date') {
      if (typeof value === 'string' && PROFILE_DATE_RE.test(value)) {
        next.dateOfBirth = value; applied.push('dateOfBirth');
      } else rejected.push({ field, reason: 'data deve ser YYYY-MM-DD' });
    } else {
      rejected.push({ field, reason: 'campo desconhecido no schema user_profile v1' });
    }
  }

  return { nextForm: next, applied, rejected };
}

// ─────────────────────────────────────────────────────────────
// Nutrition Plan (Single Assist 26/08) — schema do
// NutritionPlanBuilderWizard. Identidade (athlete_id, mode, group_*)
// NUNCA vem de patch do LLM — só de request_selection/UI.
// ─────────────────────────────────────────────────────────────

export const NUTRITION_GOALS = ['performance', 'weight_loss', 'muscle_gain', 'maintenance', 'competition', 'recovery'];
export const NUTRITION_PHASES = ['base', 'volume', 'speed', 'taper', 'competition'];
const KCAL_FIELDS = ['kcal_training', 'kcal_rest', 'kcal_competition'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Contexto do form nutrition_plan p/ o orchestrator (fill_form v1). */
export function buildNutritionPlanFormContext(form = {}) {
  return {
    form_id: 'nutrition_plan',
    schema_version: 'v1',
    current_values: {
      plan_name: form.plan_name ?? '',
      goal: form.goal ?? null,
      phase: form.phase ?? null,
      start_date: form.start_date ?? null,
      end_date: form.end_date ?? null,
      notes: form.notes ?? '',
      kcal_training: form.kcal_training ?? null,
      kcal_rest: form.kcal_rest ?? null,
      kcal_competition: form.kcal_competition ?? null,
      hydration_ml: form.hydration_ml ?? null,
      // identidade exposta só como leitura (contexto), nunca patchável
      mode: form.mode ?? 'individual',
      athlete_id: form.athlete_id ?? null,
      group_value: form.group_value ?? null,
    },
    missing_required_fields: [
      ...(!form.plan_name ? ['plan_name'] : []),
      ...(!form.athlete_id && form.mode !== 'group' ? ['athlete_id'] : []),
      ...(!form.end_date ? ['end_date'] : []),
    ],
    allowed_values: {
      goal: NUTRITION_GOALS,
      phase: NUTRITION_PHASES,
      kcal_training: '800 a 8000 kcal',
      kcal_rest: '800 a 8000 kcal',
      kcal_competition: '800 a 8000 kcal',
      hydration_ml: '500 a 10000 ml',
      start_date: 'YYYY-MM-DD',
      end_date: 'YYYY-MM-DD (>= start_date)',
    },
  };
}

/**
 * Valida patch do agente contra o schema nutrition_plan v1.
 * Mesmas regras do wellness: fora do enum/intervalo é RECUSADO (nunca
 * clampado); campo de identidade ou desconhecido é rejeitado explicitamente.
 */
export function applyNutritionPlanPatch(form, patch) {
  const applied = [];
  const rejected = [];
  const next = { ...form };

  if (!patch || typeof patch !== 'object') {
    return { nextForm: form, applied, rejected: [{ field: '*', reason: 'patch inválido' }] };
  }

  for (const [field, value] of Object.entries(patch)) {
    if (field === 'plan_name' || field === 'notes') {
      if (typeof value === 'string' && value.length <= (field === 'notes' ? 2000 : 200)) {
        next[field] = value; applied.push(field);
      } else rejected.push({ field, reason: 'texto inválido ou longo demais' });
    } else if (field === 'goal') {
      if (NUTRITION_GOALS.includes(value)) { next.goal = value; applied.push(field); }
      else rejected.push({ field, reason: `goal fora do enum (${NUTRITION_GOALS.join('/')})` });
    } else if (field === 'phase') {
      if (NUTRITION_PHASES.includes(value)) { next.phase = value; applied.push(field); }
      else rejected.push({ field, reason: `phase fora do enum (${NUTRITION_PHASES.join('/')})` });
    } else if (field === 'start_date' || field === 'end_date') {
      if (typeof value === 'string' && DATE_RE.test(value)) {
        const start = field === 'start_date' ? value : (next.start_date || '');
        const end = field === 'end_date' ? value : (next.end_date || '');
        if (start && end && end < start) {
          rejected.push({ field, reason: 'end_date anterior a start_date' });
        } else { next[field] = value; applied.push(field); }
      } else rejected.push({ field, reason: 'data deve ser YYYY-MM-DD' });
    } else if (KCAL_FIELDS.includes(field)) {
      const n = Number(value);
      if (Number.isFinite(n) && n >= 800 && n <= 8000) { next[field] = Math.round(n); applied.push(field); }
      else rejected.push({ field, reason: 'kcal fora do intervalo 800-8000' });
    } else if (field === 'hydration_ml') {
      const n = Number(value);
      if (Number.isFinite(n) && n >= 500 && n <= 10000) { next.hydration_ml = Math.round(n); applied.push(field); }
      else rejected.push({ field, reason: 'hidratação fora do intervalo 500-10000 ml' });
    } else if (['athlete_id', 'mode', 'group_type', 'group_value', 'excluded_ids'].includes(field)) {
      rejected.push({ field, reason: 'campo de identidade — use a seleção da UI, não patch do LLM' });
    } else {
      rejected.push({ field, reason: 'campo desconhecido no schema nutrition_plan v1' });
    }
  }

  return { nextForm: next, applied, rejected };
}
